/**
 * Round-trip helpers for the subscription-confirm re-auth hop (#1658).
 *
 * A reactivated subscription may be riding on dead stored tokens (nothing refreshes
 * them while the account sits inactive through a cancelled gap; an uninstall or a
 * superseding OAuth kills them outright). ShopifyConfirm therefore bounces once
 * through the normal OAuth authorize — instant, no consent screen, since the app is
 * installed and the scopes are unchanged — so /oauth/redirect stores a FRESH grant
 * and clears any Reconnect Required stamps. The redirect lands on /billing/products,
 * which consumes the stashed resume URL here and routes back to the confirm page.
 *
 * sessionStorage on purpose: the whole round trip is same-tab and takes seconds; the
 * markers must not outlive the tab (the exact lesson of #1645).
 *
 * Every helper is exception-safe: some browsers throw on ANY storage access (e.g.
 * "block all cookies" → SecurityError). Storage failure must never break activation —
 * it just skips the hop (status quo), which is also loop-safe since the marker
 * couldn't have persisted either.
 */

const RESUME_KEY = 'shopify_confirm_resume'
// The hop is a few redirects; anything older is a leftover from an abandoned flow.
const RESUME_TTL_MS = 10 * 60 * 1000

const reauthDoneKey = (chargeId: string | null) =>
  `shopify_reauth_done_${chargeId || 'no-charge'}`

/** Hop at most once per charge — a repeat visit to the confirm page must not loop. */
export const shouldReauthOnConfirm = (chargeId: string | null): boolean => {
  try {
    return sessionStorage.getItem(reauthDoneKey(chargeId)) !== '1'
  } catch (e) {
    return false // storage unavailable → no hop (status quo), no loop risk either
  }
}

export const markReauthDone = (chargeId: string | null): void => {
  try {
    sessionStorage.setItem(reauthDoneKey(chargeId), '1')
  } catch (e) { /* storage unavailable — shouldReauthOnConfirm already returns false */ }
}

export const stashConfirmResume = (url: string): void => {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify({ url, ts: Date.now() }))
  } catch (e) { /* no stash → the return leg simply won't resume */ }
}

/** Drop the stash (hop failed before navigating) so it can't hijack a later
 *  legitimate visit to /billing/products in the same tab. */
export const clearConfirmResume = (): void => {
  try {
    sessionStorage.removeItem(RESUME_KEY)
  } catch (e) { /* ignore */ }
}

/**
 * One-shot read of the stashed confirm URL. TTL guards against a stale stash from an
 * abandoned hop hijacking an unrelated later visit to /billing/products.
 */
export const consumeConfirmResume = (): string | null => {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY)
    if (!raw) return null
    sessionStorage.removeItem(RESUME_KEY)
    const { url, ts } = JSON.parse(raw)
    if (typeof url === 'string' && typeof ts === 'number' && Date.now() - ts < RESUME_TTL_MS) {
      return url
    }
  } catch (e) {
    // corrupt stash or storage unavailable — treat as absent
  }
  return null
}
